import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function AppPrivacyPolicy() {
    const navigate = useNavigate();
    const lastUpdated = 'January 21, 2026';

    return (
        <div className="px-4 pb-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F8FA] dark:bg-dark-elevated"
                >
                    <ChevronLeft className="w-5 h-5 text-[#4A5C6A]" />
                </button>
                <h1 className="text-xl font-bold text-[#1A2832] dark:text-white">Privacy Policy</h1>
            </div>

            <p className="text-sm text-[#7B8C9B] mb-6">Last updated: {lastUpdated}</p>

            <div className="bg-white dark:bg-dark-card rounded-2xl p-5 shadow-sm space-y-6">
                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        1. Introduction
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        Way2Will ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our productivity assistant application. Please read this policy carefully.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        2. Information We Collect
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed mb-2">
                        We collect information you provide directly:
                    </p>
                    <ul className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary list-disc list-inside space-y-1 mb-3">
                        <li><strong>Account Information:</strong> Name, email address, and password</li>
                        <li><strong>Task & Event Data:</strong> Tasks, events, projects, and notes you create</li>
                        <li><strong>Voice Data:</strong> Voice recordings when using voice input features</li>
                        <li><strong>Integration Data:</strong> Data from connected services (Google Calendar, Gmail, etc.)</li>
                        <li><strong>Usage Data:</strong> How you interact with the app</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        3. How We Use Your Information
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed mb-2">
                        We use your information to:
                    </p>
                    <ul className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary list-disc list-inside space-y-1">
                        <li>Provide and maintain the Service</li>
                        <li>Process voice commands and generate AI responses</li>
                        <li>Sync with your connected calendar and email accounts</li>
                        <li>Send notifications and reminders you've requested</li>
                        <li>Improve and personalize your experience</li>
                        <li>Communicate with you about updates and support</li>
                        <li>Ensure security and prevent fraud</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        4. Voice Data & AI Processing
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        When you use voice features, your audio is processed to convert speech to text and understand your commands. Voice data may be temporarily stored for processing but is not retained indefinitely unless you opt in to voice data storage for improved accuracy. You can manage voice data retention in your settings and request deletion at any time.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        5. Data Sharing & Third Parties
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed mb-2">
                        We do not sell your personal information. We may share data with:
                    </p>
                    <ul className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary list-disc list-inside space-y-1">
                        <li><strong>Service Providers:</strong> Cloud hosting, analytics, and payment processors</li>
                        <li><strong>Integration Partners:</strong> When you connect third-party services</li>
                        <li><strong>AI Services:</strong> For processing voice and text commands</li>
                        <li><strong>Legal Requirements:</strong> When required by law or to protect rights</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        6. Data Security
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        We implement industry-standard security measures to protect your data, including encryption in transit and at rest, secure authentication, and regular security audits. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        7. Data Retention
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        We retain your data for as long as your account is active or as needed to provide services. You can export your data at any time from the settings. Upon account deletion, we will delete your personal data within 30 days, except where retention is required by law.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        8. Your Rights
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed mb-2">
                        Depending on your location, you may have rights including:
                    </p>
                    <ul className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary list-disc list-inside space-y-1">
                        <li>Access to your personal data</li>
                        <li>Correction of inaccurate data</li>
                        <li>Deletion of your data</li>
                        <li>Data portability (export your data)</li>
                        <li>Opt-out of marketing communications</li>
                        <li>Withdrawal of consent</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        9. Children's Privacy
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        The Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child, please contact us immediately.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        10. Cookies & Tracking
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        We use cookies and similar technologies to maintain your session, remember your preferences, and analyze usage. You can control cookies through your browser settings, though disabling them may affect functionality.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        11. Changes to This Policy
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of the Service after changes constitutes acceptance.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        12. Contact Us
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        If you have questions about this Privacy Policy or our data practices, please contact us at:
                    </p>
                    <a
                        href="mailto:support@way2will.com"
                        className="text-[#0D5F6B] font-medium text-sm mt-2 inline-block"
                    >
                        support@way2will.com
                    </a>
                </section>
            </div>
        </div>
    );
}
